import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/config";
import { PhoneCall, ClipboardCheck, CalendarClock, Hammer, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";

const STEPS = [
  {
    icon: PhoneCall,
    label: "Step 1",
    title: "You reach out",
    body: "Fill out the quote form, call 850-252-0162, or email info@jfeva.com. Share room photos and rough dimensions if you have them — the more detail, the faster we respond.",
  },
  {
    icon: ClipboardCheck,
    label: "Step 2",
    title: "We measure & quote",
    body: "We schedule a free on-site measure (or review your photos for small jobs), confirm the scope, and send a plain, itemized estimate. No pressure, no surprise upsells — valid for 30 days.",
  },
  {
    icon: CalendarClock,
    label: "Step 3",
    title: "You pick a date",
    body: "Approve the estimate, choose a start date from our live calendar, and pay a deposit. We order materials and lock in the schedule.",
  },
  {
    icon: Hammer,
    label: "Step 4",
    title: "We install",
    body: "On install day we arrive on time, protect your floors and furniture, cut tile and plank in a clean work zone, and keep you updated. Most residential projects wrap in 1–3 days.",
  },
  {
    icon: Sparkles,
    label: "Step 5",
    title: "Walkthrough & cleanup",
    body: "When the floor is set, we walk through every room with you, address any touch-ups on the spot, haul away debris, and leave your home cleaner than we found it.",
  },
  {
    icon: ShieldCheck,
    label: "Step 6",
    title: "1-year workmanship warranty",
    body: "Every installation we do is backed by a full 1-year labor warranty. If anything about our workmanship isn't right, we come back and fix it — free.",
  },
];

export default function WhatToExpect() {
  return (
    <div data-testid="what-to-expect-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <div className="eyebrow text-[color:var(--brand-wood)]">What to expect</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            From &ldquo;hello&rdquo; to a floor you love — in six honest steps.
          </h1>
        </div>
        <p className="md:col-span-5 text-[color:var(--brand-woodDark)]">
          No pressure, no mystery pricing, and no corners cut. Here&apos;s exactly how a project with JFeva Solutions, LLC goes
          — the same way we'd explain it to a neighbor over coffee.
        </p>
      </div>

      <div className="mt-14 grid gap-5 md:grid-cols-2">
        {STEPS.map(({ icon: Icon, label, title, body }, i) => (
          <div
            key={title}
            data-testid={`step-${i + 1}`}
            className="rounded-2xl border border-black/10 bg-white p-8 hover-lift"
          >
            <div className="flex items-start gap-5">
              <div className="h-12 w-12 shrink-0 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center">
                <Icon className="h-5 w-5"/>
              </div>
              <div className="min-w-0">
                <div className="eyebrow text-[color:var(--brand-wood)]">{label}</div>
                <h3 className="mt-2 font-display text-2xl leading-tight">{title}</h3>
                <p className="mt-3 text-sm text-[color:var(--brand-woodDark)] leading-relaxed">{body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Warranty strip */}
      <div className="mt-16 rounded-3xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-10 lg:p-14 grid md:grid-cols-12 items-center gap-8">
        <div className="md:col-span-8">
          <div className="eyebrow text-white/60">1-Year Labor Warranty</div>
          <h3 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
            Our workmanship is backed in writing.
          </h3>
          <p className="mt-3 text-white/70 text-sm max-w-2xl">
            Every floor and tile install we complete is covered for one full year of labor. If our workmanship fails
            — a loose plank, a cracked grout line, anything — we come back and make it right. No fine print.
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-3">
          <Link data-testid="to-warranty-btn" to="/warranty"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-cream)] text-[color:var(--brand-ink)] px-5 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
            See warranty details <ArrowRight className="h-4 w-4"/>
          </Link>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-10 flex flex-wrap items-center gap-4">
        <img src={IMAGES.kitchen} alt="" className="hidden sm:block h-20 w-20 rounded-xl object-cover"/>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display text-xl leading-tight">Ready to start step one?</div>
          <div className="text-sm text-[color:var(--brand-woodDark)]">We reply to every quote within one business day.</div>
        </div>
        <Link data-testid="wte-quote-btn" to="/quote"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
          Request a Quote <ArrowRight className="h-4 w-4"/>
        </Link>
      </div>
    </div>
  );
}
